import * as createSubcategories from 'store/actions/subcategories/createSubcategories';
import * as deleteSubcatgories from 'store/actions/subcategories/deleteSubcategories';
import * as fetchSubcategories from 'store/actions/subcategories/fetchSubcategories';
import * as types from 'store/actions/types';
import * as updateSubcategories from 'store/actions/subcategories/updateSubcategories';
import apiClient from 'clients/apiClient';
import config from 'helpers/config';
import utils from 'helpers/utils';
import { put, takeEvery } from 'redux-saga/effects';
import { showNotification } from 'store/actions/uiActions';

function* fetchSubcategoriesAsync({ payload }) {
    try {
        const url = `${ config.getApiBaseUrl() }/subcategories`;

        const response = yield apiClient.read(url, payload);

        yield put(fetchSubcategories.fetchSubcategoriesSuccess(response));
    } catch (error) {
        yield put(fetchSubcategories.fetchSubcategoriesFailure(error));
    }
}

function* fetchSubcategoryAsync({ payload }) {
    const {
        id,
        onError,
    } = payload;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/subcategories/${ id }`;

        // Fetch the data.
        const response = yield apiClient.read(url);

        // Send the data to the subcategory.
        yield put(fetchSubcategories.fetchSubcategorySuccess(response));

        // Trigger the localisations of that subcategory.
        yield put(fetchSubcategories.fetchSubcategoryLocalisation(payload, response));
    } catch (error) {
        // Trigger the error action.
        yield put(fetchSubcategories.fetchSubcategoryFailure(error, payload));

        if (utils.isFunction(onError)) {
            onError(error);
        }
    }
}

function* fetchSubcategoryLocalisationAsync({ payload }) {
    const {
        id,
        onError,
        onSuccess,
    } = payload;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/subcategories/${ id }/localisation`;

        // Fetch the data.
        const response = yield apiClient.read(url);

        // Send the data to the success action.
        yield put(fetchSubcategories.fetchSubcategoryLocalisationSuccess(payload, response));

        if (utils.isFunction(onSuccess)) {
            onSuccess();
        }
    } catch (error) {
        // Trigger the error action.
        yield put(fetchSubcategories.fetchSubcategoryLocalisationFailure(error, payload));

        if (utils.isFunction(onError)) {
            onError(error);
        }
    }
}

function* createSubcategoryAsync({ payload }) {
    const {
        onError,
        formValues,
    } = payload;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/subcategories`;

        // Fetch the data.
        const response = yield apiClient.create(url, formValues.base);

        // Send the data to the success action.
        yield put(createSubcategories.createSubcategoryLocalisation(payload, response));
    } catch (error) {
        // Trigger the error action.
        yield put(createSubcategories.createSubcategoryFailure(error));

        yield onError(error);
    }
}

function* createSubcategoryLocalisationAsync({ payload, response }) {
    const {
        formValues,
        onError,
        onSuccess,
    } = payload;

    const {
        id,
    } = response;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/subcategories/${ id }/localisation`;

        // Update the data.
        yield apiClient.update(url, formValues.localisation);

        // Send the data to the success action.
        yield put(createSubcategories.createSubcategoryLocalisationSuccess(payload, response));

        yield onSuccess(response);
    } catch (error) {
        // Trigger the error action.
        yield put(createSubcategories.createSubcategoryLocalisationFailure(error));

        yield onError(error);
    }
}

function* createSubcategoryLocalisationSuccessAsync() {
    yield put(showNotification('notifications.subcategories.create.success'));
}

function* updateSubcategoryAsync({ payload }) {
    const {
        formValues,
        id,
        onError,
    } = payload;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/subcategories/${ id }`;

        // Patch the data.
        const response = yield apiClient.patch(url, formValues.base);

        // Send the data to the success action.
        yield put(updateSubcategories.updateSubcategoryLocalisation(payload, response));
    } catch (error) {
        // Trigger the error action.
        yield put(updateSubcategories.updateSubcategoryFailure(error));

        yield onError(error);
    }
}

function* updateSubcategoryLocalisationAsync({ payload }) {
    const {
        formValues,
        id,
        onError,
        onSuccess,
    } = payload;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/subcategories/${ id }/localisation`;

        // Update the data.
        const response = yield apiClient.update(url, formValues.localisation);

        // Send the data to the success action.
        yield put(updateSubcategories.updateSubcategoryLocalisationSuccess(payload, response));

        yield onSuccess();
    } catch (error) {
        // Trigger the error action.
        yield put(updateSubcategories.updateSubcategoryLocalisationFailure(error));

        yield onError(error);
    }
}

function* updateSubcategoryLocalisationSuccessAsync() {
    yield put(showNotification('notifications.subcategories.update.success'));
}

function* deleteSubcategoryAsync({ payload }) {
    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/subcategories/${ payload.id }`;

        // Delete the data.
        yield apiClient.delete(url);

        yield put(deleteSubcatgories.deleteSubcategorySuccess(payload));
    } catch (error) {
        // Trigger the error action.
        yield put(deleteSubcatgories.deleteSubcategoryFailure(error));
    }
}

function* deleteSubcategorySuccessAsync() {
    yield put(showNotification('notifications.subcategories.delete.success', 'success'));
}

function* failureMessage({ error }) {
    const { message } = error.response;

    if (!message) {
        return;
    }

    yield put(showNotification(message, 'warning'));
}

export default function* activitiesSagas() {
    yield [
        takeEvery(types.FETCH_SUBCATEGORIES, fetchSubcategoriesAsync),
        takeEvery(types.FETCH_SUBCATEGORIES_FAILURE, failureMessage),

        takeEvery(types.FETCH_SUBCATEGORY, fetchSubcategoryAsync),
        takeEvery(types.FETCH_SUBCATEGORY_FAILURE, failureMessage),

        takeEvery(types.FETCH_SUBCATEGORY_LOCALISATION, fetchSubcategoryLocalisationAsync),
        takeEvery(types.FETCH_SUBCATEGORY_LOCALISATION_FAILURE, failureMessage),

        takeEvery(types.CREATE_SUBCATEGORY, createSubcategoryAsync),
        takeEvery(types.CREATE_SUBCATEGORY_FAILURE, failureMessage),

        takeEvery(types.CREATE_SUBCATEGORY_LOCALISATION, createSubcategoryLocalisationAsync),
        takeEvery(types.CREATE_SUBCATEGORY_LOCALISATION_FAILURE, failureMessage),
        takeEvery(types.CREATE_SUBCATEGORY_LOCALISATION_SUCCESS, createSubcategoryLocalisationSuccessAsync),

        takeEvery(types.UPDATE_SUBCATEGORY, updateSubcategoryAsync),
        takeEvery(types.UPDATE_SUBCATEGORY_FAILURE, failureMessage),

        takeEvery(types.UPDATE_SUBCATEGORY_LOCALISATION, updateSubcategoryLocalisationAsync),
        takeEvery(types.UPDATE_SUBCATEGORY_LOCALISATION_FAILURE, failureMessage),
        takeEvery(types.UPDATE_SUBCATEGORY_LOCALISATION_SUCCESS, updateSubcategoryLocalisationSuccessAsync),

        takeEvery(types.DELETE_SUBCATEGORY, deleteSubcategoryAsync),
        takeEvery(types.DELETE_SUBCATEGORY_FAILURE, failureMessage),
        takeEvery(types.DELETE_SUBCATEGORY_SUCCESS, deleteSubcategorySuccessAsync),
    ];
}



// WEBPACK FOOTER //
// ./src/store/sagas/subcategoriesSagas.js